if (!customElements.get("free-shipping-progress")) {
  customElements.define(
    "free-shipping-progress",
    class FreeShippingProgress extends HTMLElement {
      constructor() {
        super();

        this.refreshTimer = null;
        this.pubSubTimer = null;
        this.pubSubAttempts = 0;
        this.unsubscribeCartUpdate = null;
        this.abortController = null;

        this.handleCartUpdate = this.handleCartUpdate.bind(this);
        this.handlePageShow = this.handlePageShow.bind(this);
      }

      connectedCallback() {
        this.messageElement = this.querySelector(
          "[data-free-shipping-message]",
        );

        this.fillElement = this.querySelector("[data-free-shipping-fill]");

        this.progressElement = this.querySelector("[data-free-shipping-track]");

        /*
         * Threshold is supplied by Liquid in the currency's
         * smallest unit. For INR, ₹1,500 becomes 150000.
         */
        this.threshold = Number(this.dataset.threshold) || 0;

        this.incompleteMessage =
          this.dataset.incompleteMessage ||
          "Spend [amount] more to unlock Free Shipping";

        this.completeMessage =
          this.dataset.completeMessage || "You’ve unlocked Free Shipping!";

        this.currency = this.dataset.currency || "INR";
        this.locale = this.dataset.locale || "en-IN";

        if (
          !this.messageElement ||
          !this.fillElement ||
          !this.progressElement ||
          this.threshold <= 0
        ) {
          this.hidden = true;
          return;
        }

        this.hidden = false;

        this.bindEvents();
        this.scheduleRefresh(0);
      }

      disconnectedCallback() {
        window.clearTimeout(this.refreshTimer);
        window.clearTimeout(this.pubSubTimer);

        this.abortController?.abort();

        window.removeEventListener(
          "ashpveda:cart-updated",
          this.handleCartUpdate,
        );

        window.removeEventListener("pageshow", this.handlePageShow);

        document.removeEventListener(
          "shopify:cart:lines-update",
          this.handleCartUpdate,
        );

        document.removeEventListener(
          "shopify:cart:refresh",
          this.handleCartUpdate,
        );

        document.removeEventListener("cart:updated", this.handleCartUpdate);

        if (typeof this.unsubscribeCartUpdate === "function") {
          this.unsubscribeCartUpdate();
        }

        this.unsubscribeCartUpdate = null;
      }

      bindEvents() {
        window.addEventListener("ashpveda:cart-updated", this.handleCartUpdate);

        window.addEventListener("pageshow", this.handlePageShow);

        /*
         * Shopify standard cart event.
         */
        document.addEventListener(
          "shopify:cart:lines-update",
          this.handleCartUpdate,
        );

        /*
         * Theme and app compatibility events.
         */
        document.addEventListener(
          "shopify:cart:refresh",
          this.handleCartUpdate,
        );

        document.addEventListener("cart:updated", this.handleCartUpdate);

        this.bindThemePubSub();
      }

      bindThemePubSub() {
        if (this.unsubscribeCartUpdate) return;

        if (
          typeof subscribe === "function" &&
          typeof PUB_SUB_EVENTS !== "undefined" &&
          PUB_SUB_EVENTS.cartUpdate
        ) {
          this.unsubscribeCartUpdate = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.handleCartUpdate,
          );

          return;
        }

        /*
         * The theme's pub/sub file might load after this asset.
         * Retry briefly instead of failing.
         */
        if (this.pubSubAttempts < 20) {
          this.pubSubAttempts += 1;

          this.pubSubTimer = window.setTimeout(() => {
            this.bindThemePubSub();
          }, 250);
        }
      }

      handleCartUpdate() {
        /*
         * Give Shopify a brief moment to finish updating
         * the server-side cart before requesting /cart.js.
         */
        this.scheduleRefresh(150);
      }

      handlePageShow() {
        this.scheduleRefresh(0);
      }

      scheduleRefresh(delay = 150) {
        window.clearTimeout(this.refreshTimer);

        this.refreshTimer = window.setTimeout(() => {
          this.refresh();
        }, delay);
      }

      getCartUrl() {
        let root =
          window.Shopify?.routes?.root || window.routes?.root_url || "/";

        if (!root.endsWith("/")) {
          root += "/";
        }

        return `${root}cart.js`;
      }

      async refresh() {
        this.abortController?.abort();

        this.abortController = new AbortController();

        try {
          const response = await fetch(this.getCartUrl(), {
            method: "GET",
            credentials: "same-origin",
            cache: "no-store",
            headers: {
              Accept: "application/json",
            },
            signal: this.abortController.signal,
          });

          if (!response.ok) {
            throw new Error(
              `Cart request failed with status ${response.status}`,
            );
          }

          const cart = await response.json();

          if (this.abortController.signal.aborted) return;

          this.render(cart);
        } catch (error) {
          if (error.name === "AbortError") return;

          console.error("[Ashpveda free shipping progress]", error);
        }
      }

      render(cart) {
        const cartTotal = Number(cart?.total_price) || 0;

        const remaining = Math.max(this.threshold - cartTotal, 0);

        const progress = Math.min(
          Math.max((cartTotal / this.threshold) * 100, 0),
          100,
        );

        const isComplete = remaining <= 0;

        const currency = cart?.currency || this.currency || "INR";

        const formattedRemaining = this.formatMoney(remaining, currency);

        const message = isComplete
          ? this.completeMessage
          : this.incompleteMessage.replace(/\[amount\]/gi, formattedRemaining);

        this.messageElement.textContent = message;

        this.fillElement.style.width = `${progress}%`;

        this.progressElement.setAttribute(
          "aria-valuenow",
          String(Math.round(progress)),
        );

        this.progressElement.setAttribute("aria-label", message);

        this.classList.toggle("is-complete", isComplete);

        this.dataset.cartTotal = String(cartTotal);
        this.dataset.remaining = String(remaining);
      }

      formatMoney(amountInSubunits, currency) {
        const amount = amountInSubunits / 100;

        const hasDecimalValue = Math.abs(amount - Math.round(amount)) > 0.001;

        try {
          return new Intl.NumberFormat(this.locale, {
            style: "currency",
            currency,
            minimumFractionDigits: hasDecimalValue ? 2 : 0,
            maximumFractionDigits: hasDecimalValue ? 2 : 0,
          }).format(amount);
        } catch (error) {
          return `₹${Math.round(amount).toLocaleString("en-IN")}`;
        }
      }
    },
  );
}
